import { forwardRef, Ref, ReactNode } from "react";
import { CardActions } from "@remo-co/ui-core/src/components/CardActions";
import { CardContent } from "@remo-co/ui-core/src/components/CardContent";
import { CardMedia } from "@remo-co/ui-core/src/components/CardMedia";
import { Tooltip } from "@remo-co/ui-core/src/components/Tooltip";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { User01 as User01Icon } from "@remo-co/ui-core/src/icons/User01";
import { useI18n } from "i18n";
// TODO: no ui-core component are compatible with the custom styling in this component, it must be refactored to not rely on MUI
import Card from "@mui/material/Card";
import { useStyles } from "./EventCardStyles";
import { formatDateTime } from "./utils";

interface EventCardData {
  name: string;
  imageURL?: string;
  shortDescription?: string;
  startTime?: number;
  endTime?: number;
  timezone?: string;
  capacity?: number;
  numAttendees?: number;
  numSponsors?: number;
  numFloors?: number;
  isUnlimitedEvent?: boolean;
}

interface Props {
  data: EventCardData;
  children?: ReactNode;
}

const EventCard = forwardRef(
  ({ data, children }: Props, ref?: Ref<HTMLDivElement>) => {
    const { t } = useI18n(["common", "onboarding", "event"]);
    const styles = useStyles();

    return (
      <Card ref={ref} className={styles.eventContainer}>
        <CardMedia
          className={styles.eventImage}
          image={data.imageURL}
          title={data.name}
        />
        <CardContent className={styles.eventContent}>
          <Typography className={styles.eventText} variant="h3">
            <Tooltip
              title={data.name}
              placement="top"
              enterDelay={750}
              leaveDelay={200}
            >
              <span>{data.name}</span>
            </Tooltip>
          </Typography>
          {data?.isUnlimitedEvent ? (
            <Typography
              className={styles.eventText}
              color="textSecondary"
              variant="body1"
              data-testid="event-open-24-7"
            >
              <Tooltip
                title={t("event:open.24.7")}
                placement="top"
                enterDelay={750}
                leaveDelay={200}
              >
                <Typography variant="h4">{t("event:open.24.7")}</Typography>
              </Tooltip>
            </Typography>
          ) : (
            <>
              <Typography className={styles.eventText} variant="body1">
                <Tooltip
                  title={data.shortDescription || ""}
                  placement="top"
                  enterDelay={750}
                  leaveDelay={200}
                >
                  <span>{data.shortDescription}</span>
                </Tooltip>
              </Typography>
              <div>
                <Typography variant="caption">
                  {formatDateTime(data.startTime, data.timezone)}
                  <br />
                  {formatDateTime(data.endTime, data.timezone)}
                </Typography>
              </div>
            </>
          )}
        </CardContent>
        {(data.capacity || data.numSponsors || data.numFloors) && (
          <CardActions className={styles.footer}>
            <Tooltip
              title={`This event has ${data.numAttendees} confirmed guests`}
              placement="top"
              enterDelay={750}
              leaveDelay={200}
            >
              <Typography variant="h5" className={styles.footerContent}>
                <User01Icon className={styles.personIcon} />
                {` ${data.numAttendees}/${data.capacity}`}
              </Typography>
            </Tooltip>
            <Typography variant="h5">
              {`${data.numFloors} ${
                (data.numFloors ?? 0) > 1
                  ? t("onboarding:floors")
                  : t("onboarding:floor")
              }`}
            </Typography>
          </CardActions>
        )}
        {children}
      </Card>
    );
  },
);

export default EventCard;
